'use client';

import { useMemo, useState } from 'react';
import { ChevronDown, Check, Plus, Trash2, UserMinus } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

interface Team {
  TeamUUID: string;
  TeamName: string;
}

interface TeamSelectorProps {
  currentTeamName?: string;
  teams: Team[];
  isAdmin: boolean;
  /** Create and delete teams (Owners only). Defaults to `isAdmin`. */
  canManageTeams?: boolean;
  /** Show a "Remove from team" action (the row has a team the viewer may clear). */
  canRemoveFromTeam?: boolean;
  onSelectTeam: (teamName: string) => void;
  onCreateTeam: () => void;
  onDeleteTeam: (team: Team) => void;
}

export function TeamSelector({
  currentTeamName,
  teams,
  isAdmin,
  canManageTeams = isAdmin,
  canRemoveFromTeam = false,
  onSelectTeam,
  onCreateTeam,
  onDeleteTeam,
}: TeamSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();

  const filteredTeams = useMemo(() => {
    if (!trimmedQuery) return teams;
    const q = trimmedQuery.toLowerCase();
    return teams.filter((t) => t.TeamName.toLowerCase().includes(q));
  }, [teams, trimmedQuery]);

  const exactMatch = useMemo(
    () =>
      teams.some(
        (t) => t.TeamName.toLowerCase() === trimmedQuery.toLowerCase()
      ),
    [teams, trimmedQuery]
  );

  const closeAndReset = () => {
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="outline"
            disabled={!isAdmin}
            role="combobox"
            aria-expanded={open}
            className={cn(
              'flex items-center justify-between w-32 font-normal',
              !isAdmin && 'cursor-not-allowed opacity-50'
            )}
          >
            <span className="truncate">{currentTeamName || 'No Team'}</span>
            <ChevronDown className="w-4 h-4 text-ds-text-subtle shrink-0" />
          </Button>
        }
      />
      <PopoverContent
        align="start"
        className="w-auto min-w-40 max-w-72 p-0 bg-ds-surface-white"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search teams…"
            value={query}
            onValueChange={setQuery}
            className="h-9 text-sm"
          />
          <CommandList className="max-h-56">
            {filteredTeams.length === 0 && !canManageTeams && (
              <CommandEmpty>No teams found.</CommandEmpty>
            )}
            {canRemoveFromTeam && currentTeamName && !trimmedQuery && (
              <>
                <CommandGroup>
                  <CommandItem
                    value="__remove_from_team"
                    onSelect={() => {
                      onSelectTeam('No Team');
                      closeAndReset();
                    }}
                    className="text-sm text-ds-danger-default"
                  >
                    <UserMinus className="mr-2 h-4 w-4" />
                    Remove from team
                  </CommandItem>
                </CommandGroup>
                {filteredTeams.length > 0 && <CommandSeparator />}
              </>
            )}
            {filteredTeams.length > 0 && (
              <CommandGroup>
                {filteredTeams.map((team) => (
                  <CommandItem
                    key={team.TeamUUID}
                    value={team.TeamName}
                    onSelect={() => {
                      onSelectTeam(team.TeamName);
                      closeAndReset();
                    }}
                    className="group text-sm text-ds-text-default"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        currentTeamName === team.TeamName
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <span className="flex-1 truncate pr-6">{team.TeamName}</span>
                    {canManageTeams && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteTeam(team);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-ds-text-subtler hover:bg-ds-danger-subtlest hover:text-ds-danger-default"
                        title="Delete team"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {canManageTeams && (
              <>
                {filteredTeams.length > 0 && <CommandSeparator />}
                <CommandGroup>
                  {trimmedQuery && !exactMatch ? (
                    <CommandItem
                      value={`__create_${trimmedQuery}`}
                      onSelect={() => {
                        onSelectTeam(`CREATE_NEW_${trimmedQuery}`);
                        closeAndReset();
                      }}
                      className="text-sm text-ds-text-default"
                    >
                      <Plus className="w-3.5 h-3.5 mr-2" />
                      Create new team &ldquo;{trimmedQuery}&rdquo;
                    </CommandItem>
                  ) : (
                    <CommandItem
                      value="__create_new_team"
                      onSelect={() => {
                        onCreateTeam();
                        closeAndReset();
                      }}
                      className="text-sm text-ds-text-default"
                    >
                      <Plus className="w-3.5 h-3.5 mr-2" />
                      Create New Team
                    </CommandItem>
                  )}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
